import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar, Box, Button, Chip, CircularProgress, Paper, Stack, Tab, Tabs,
  TextField, Typography,
} from '@mui/material';
import {
  BusinessRounded, CheckCircleRounded, EventRounded, MarkEmailReadRounded,
  NotificationsRounded, PersonAddAlt1Rounded, SearchRounded, SettingsRounded,
  ShoppingBagRounded, SupportAgentRounded,
} from '@mui/icons-material';
import { arrayUnion, collection, doc, onSnapshot, query, updateDoc, where, writeBatch } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { friendlyError } from '../../utils/userMessages';

const T = {
  ink:'#182235', muted:'#6B7A8C', line:'rgba(31,62,82,.09)', canvas:'#F2F7F9',
  blue:'#2F6FED', blueSoft:'#E8F0FF', green:'#16865D', greenSoft:'#E2F4EC',
  amber:'#C87817', amberSoft:'#FFF0D7', purple:'#7D55C7', purpleSoft:'#F0E9FB',
  red:'#C44545', redSoft:'#FBE9E9',
};

const TYPE_META = {
  platform_user_registered: { label:'Registration', icon:PersonAddAlt1Rounded, color:T.blue, soft:T.blueSoft },
  platform_company_registered: { label:'Company', icon:BusinessRounded, color:T.green, soft:T.greenSoft },
  meeting_request: { label:'Meeting', icon:EventRounded, color:T.purple, soft:T.purpleSoft },
  meeting_availability_requested: { label:'Meeting', icon:EventRounded, color:T.purple, soft:T.purpleSoft },
  meeting_booked: { label:'Meeting', icon:EventRounded, color:T.purple, soft:T.purpleSoft },
  ad_submitted: { label:'Advertisement', icon:ShoppingBagRounded, color:T.amber, soft:T.amberSoft },
  support: { label:'Support', icon:SupportAgentRounded, color:T.green, soft:T.greenSoft },
  system: { label:'System', icon:SettingsRounded, color:T.muted, soft:'#EEF2F5' },
};

const toDate = (value) => value?.toDate?.() || (value?.seconds ? new Date(value.seconds * 1000) : value ? new Date(value) : new Date());
const niceTime = (value) => {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined,{dateStyle:'medium',timeStyle:'short'}).format(date);
};
const normalize = (id, row={}, viewerId='') => ({
  id, ...row, title:row.title || 'TELCOFAIR notification', body:row.body || row.message || '',
  createdAt:row.createdAt || row.timestamp || null,
  read: row.userId === 'all' ? Boolean(row.readBy?.includes?.(viewerId)) : Boolean(row.read),
  type:row.type || 'system',
});
const safeInternalPath = (value='') => {
  const path = String(value || '').trim();
  if (!path || !path.startsWith('/') || path.startsWith('//')) return '';
  return path;
};
const routeFor = (n) => {
  if (n.link) return safeInternalPath(n.link);
  if (n.registeredUserId) return `/users/${n.registeredUserId}`;
  if (n.companyId) return `/companies/${n.companyId}`;
  if (String(n.type).includes('meeting')) return '/meetings';
  if (String(n.type).includes('ad')) return '/ads';
  if (String(n.type).includes('support')) return '/admin/support';
  return '';
};

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows,setRows] = useState([]);
  const [loading,setLoading] = useState(true);
  const [tab,setTab] = useState('all');
  const [search,setSearch] = useState('');

  useEffect(()=>{
    if (!user?.uid) return undefined;
    setLoading(true);
    const buckets = { personal:[], global:[] };
    const apply = () => {
      const map = new Map();
      [...buckets.personal,...buckets.global].forEach((row)=>map.set(row.id,row));
      const next=[...map.values()].sort((a,b)=>toDate(b.createdAt)-toDate(a.createdAt));
      setRows(next); setLoading(false);
    };
    const personalQ=query(collection(db,'notifications'),where('userId','==',user.uid));
    const globalQ=query(collection(db,'notifications'),where('userId','==','all'));
    const unsubs=[
      onSnapshot(personalQ,(snap)=>{buckets.personal=snap.docs.map((d)=>normalize(d.id,d.data(),user.uid));apply();},(err)=>{console.error(err);setLoading(false);}),
      onSnapshot(globalQ,(snap)=>{buckets.global=snap.docs.map((d)=>normalize(d.id,d.data(),user.uid));apply();},(err)=>console.warn('Global notifications unavailable:',err?.message||err)),
    ];
    return ()=>unsubs.forEach((u)=>u());
  },[user?.uid]);

  const filtered=useMemo(()=>rows.filter((n)=>{
    if(tab==='unread'&&n.read)return false;
    if(tab==='registrations'&&!['platform_user_registered','platform_company_registered'].includes(n.type))return false;
    if(tab==='meetings'&&!String(n.type).includes('meeting'))return false;
    if(tab==='commercial'&&!/(ad|subscription|payment|sponsor)/i.test(String(n.type)))return false;
    const q=search.trim().toLowerCase();
    return !q || `${n.title} ${n.body} ${n.type}`.toLowerCase().includes(q);
  }),[rows,tab,search]);
  const unread=rows.filter((n)=>!n.read).length;

  const markRead=async(n)=>{
    if(n.read)return;
    try{
      if(n.userId==='all') await updateDoc(doc(db,'notifications',n.id),{readBy:arrayUnion(user.uid)});
      else await updateDoc(doc(db,'notifications',n.id),{read:true});
    }catch(err){console.warn('Mark notification read failed:',err?.message||err);}
  };
  const open=async(n)=>{await markRead(n);const path=routeFor(n);if(path)navigate(path);};
  const markAll=async()=>{
    const pending=rows.filter((n)=>!n.read);
    if(!pending.length)return;
    try{
      for(let offset=0;offset<pending.length;offset+=450){
        const batch=writeBatch(db);
        pending.slice(offset,offset+450).forEach((n)=>batch.update(
          doc(db,'notifications',n.id),
          n.userId==='all' ? {readBy:arrayUnion(user.uid)} : {read:true}
        ));
        await batch.commit();
      }
      toast.success('Notifications marked as read.');
    }catch(err){toast.error(friendlyError(err,'Unable to update notifications.'));}
  };

  return (
    <Box sx={{fontFamily:"'Plus Jakarta Sans','Inter',sans-serif",color:T.ink}}>
      <Box sx={{display:'flex',alignItems:{xs:'flex-start',md:'center'},justifyContent:'space-between',gap:2,flexDirection:{xs:'column',md:'row'},mb:2.2}}>
        <Box>
          <Typography sx={{fontSize:'.66rem',fontWeight:900,letterSpacing:'.18em',textTransform:'uppercase',color:T.blue}}>Platform activity</Typography>
          <Typography sx={{fontSize:{xs:'1.65rem',md:'2rem'},fontWeight:900,letterSpacing:'-.04em',mt:.3}}>Notifications</Typography>
          <Typography sx={{fontSize:'.78rem',color:T.muted,mt:.35}}>New registrations, companies, meetings, commercial events and system activity in one place.</Typography>
        </Box>
        <Button variant="contained" disableElevation startIcon={<MarkEmailReadRounded/>} onClick={markAll} disabled={!unread} sx={{borderRadius:2.8,fontWeight:850,textTransform:'none',px:2.1,color:'#fff',background:'linear-gradient(135deg,#2F6FED,#6A5FD2)',boxShadow:'0 10px 24px rgba(47,111,237,.20)','&:hover':{background:'linear-gradient(135deg,#285FCF,#5B50BE)',boxShadow:'0 13px 28px rgba(47,111,237,.24)'}}}>Mark all read {unread?`(${unread})`:''}</Button>
      </Box>

      <Paper elevation={0} sx={{position:'relative',border:`1px solid ${T.line}`,borderRadius:4,bgcolor:'rgba(255,255,255,.92)',overflow:'hidden',boxShadow:'0 18px 48px -30px rgba(25,57,78,.34)','&::before':{content:'""',position:'absolute',inset:'0 0 auto 0',height:3,background:'linear-gradient(90deg,#2F6FED,#7D55C7,#16865D,#C87817)',zIndex:2}}}>
        <Box sx={{p:{xs:1.25,md:1.55},borderBottom:`1px solid ${T.line}`,display:'flex',gap:1.2,alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',background:'linear-gradient(180deg,rgba(248,251,253,.96),rgba(255,255,255,.78))'}}>
          <Tabs value={tab} onChange={(_,v)=>setTab(v)} variant="scrollable" scrollButtons="auto" sx={{minHeight:38,'& .MuiTabs-indicator':{height:0},'& .MuiTab-root':{minHeight:36,textTransform:'none',fontWeight:850,fontSize:'.7rem',borderRadius:2.2,mr:.45,px:1.2,color:T.muted,border:`1px solid transparent`,'&.Mui-selected':{color:T.blue,bgcolor:T.blueSoft,borderColor:'rgba(47,111,237,.13)',boxShadow:'inset 0 1px 0 rgba(255,255,255,.7)'}}}}>
            <Tab value="all" label={`All ${rows.length}`} />
            <Tab value="unread" label={`Unread ${unread}`} />
            <Tab value="registrations" label="Registrations" />
            <Tab value="meetings" label="Meetings" />
            <Tab value="commercial" label="Commercial" />
          </Tabs>
          <TextField size="small" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search notifications" sx={{width:{xs:'100%',sm:275},'& .MuiOutlinedInput-root':{borderRadius:2.8,bgcolor:'#fff',boxShadow:'0 5px 14px rgba(25,57,78,.055)','& fieldset':{borderColor:T.line},'&:hover fieldset':{borderColor:'rgba(47,111,237,.28)'}}}} slotProps={{input:{startAdornment:<SearchRounded sx={{fontSize:18,color:T.muted,mr:.7}}/>}}}/>
        </Box>

        {loading ? <Box sx={{minHeight:280,display:'grid',placeItems:'center'}}><CircularProgress/></Box> : filtered.length===0 ? (
          <Box sx={{minHeight:300,display:'grid',placeItems:'center',textAlign:'center',p:3}}>
            <Box><NotificationsRounded sx={{fontSize:42,color:'#A8B5C2'}}/><Typography sx={{fontWeight:900,mt:1}}>No notifications here</Typography><Typography sx={{fontSize:'.75rem',color:T.muted,mt:.4}}>New user/company registrations and platform events will appear automatically.</Typography></Box>
          </Box>
        ) : (
          <Stack spacing={1} sx={{p:{xs:1,md:1.25},background:'linear-gradient(180deg,#FBFDFE,#F5F9FB)'}}>
            {filtered.map((n)=>{
              const meta=TYPE_META[n.type] || (/meeting/.test(n.type)?TYPE_META.meeting_request:/company/.test(n.type)?TYPE_META.platform_company_registered:TYPE_META.system);
              const Icon=meta.icon; const route=routeFor(n);
              return (
                <Box
                  key={n.id}
                  onClick={()=>open(n)}
                  onKeyDown={(e)=>{if(route&&(e.key==='Enter'||e.key===' ')){e.preventDefault();open(n);}}}
                  role={route?'button':undefined}
                  tabIndex={route?0:undefined}
                  sx={{
                    position:'relative',overflow:'hidden',p:{xs:1.35,md:1.55},display:'grid',
                    gridTemplateColumns:'auto minmax(0,1fr) auto',gap:1.25,alignItems:'start',
                    bgcolor:'#fff',cursor:route?'pointer':'default',borderRadius:3,
                    border:`1px solid ${n.read?T.line:`${meta.color}24`}`,
                    boxShadow:n.read?'0 5px 14px rgba(25,57,78,.045)':`0 10px 24px ${meta.color}12`,
                    transition:'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
                    '&::before':{content:'""',position:'absolute',left:0,top:0,bottom:0,width:n.read?3:5,bgcolor:n.read?'#D5DEE6':meta.color},
                    '&::after':{content:'""',position:'absolute',width:120,height:120,borderRadius:'50%',right:-55,top:-70,background:`${meta.soft}88`,pointerEvents:'none'},
                    '&:hover':route?{transform:'translateY(-2px)',boxShadow:`0 14px 30px ${meta.color}18`,borderColor:`${meta.color}30`}:undefined,
                    '&:focus-visible':{outline:`2px solid ${meta.color}`,outlineOffset:2},
                  }}
                >
                  <Avatar sx={{width:44,height:44,bgcolor:meta.soft,color:meta.color,border:`1px solid ${meta.color}24`,boxShadow:`0 7px 16px ${meta.color}14`,zIndex:1}}><Icon fontSize="small"/></Avatar>
                  <Box sx={{minWidth:0,zIndex:1}}>
                    <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap">
                      <Typography component="div" sx={{fontSize:'.82rem',fontWeight:900,color:T.ink,letterSpacing:'-.01em'}}>{n.title}</Typography>
                      {!n.read?<Box sx={{width:7,height:7,borderRadius:'50%',bgcolor:meta.color,boxShadow:`0 0 0 4px ${meta.soft}`}}/>:null}
                    </Stack>
                    <Typography component="div" sx={{fontSize:'.73rem',lineHeight:1.58,color:T.muted,mt:.3,maxWidth:860}}>{n.body}</Typography>
                    <Stack direction="row" spacing={.7} mt={.8} alignItems="center" flexWrap="wrap">
                      <Chip size="small" label={meta.label} sx={{height:22,fontSize:'.59rem',fontWeight:900,bgcolor:meta.soft,color:meta.color,border:`1px solid ${meta.color}18`}}/>
                      <Typography component="span" sx={{fontSize:'.61rem',color:'#8A98A8',fontWeight:650}}>{niceTime(n.createdAt)}</Typography>
                      {n.userId==='all'?<Chip size="small" label="Platform-wide" sx={{height:20,fontSize:'.54rem',fontWeight:800,bgcolor:'#EEF2F5',color:T.muted}}/>:null}
                    </Stack>
                  </Box>
                  <Box sx={{pt:.35,zIndex:1}}>{n.read?<CheckCircleRounded sx={{fontSize:17,color:'#A6B2BE'}}/>:<NotificationsRounded sx={{fontSize:18,color:meta.color}}/>}</Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
